import axios from 'axios';
import API_URL from './config';
import logger from './utils/logger';


// Create a logging utility that respects production mode
const apiLogger = {
  log: (message, ...args) => {
    if (process.env.NODE_ENV !== 'production') {
      logger.debug(message, ...args);
    }
  },
  warn: (message, ...args) => {
    if (process.env.NODE_ENV !== 'production') {
      logger.warn(message, ...args);
    } else {
      // In production, we might want to keep warnings but sanitize them
      logger.warn(message);
    }
  },
  error: (message, error) => {
    if (process.env.NODE_ENV !== 'production') {
      logger.error(message, error);
    } else {
      // In production, log errors but without sensitive details
      logger.error(message);
    }
  },
  debug: (message, ...args) => {
    if (process.env.NODE_ENV !== 'production') {
      logger.debug(message, ...args);
    }
  }
};

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: API_URL.replace(/\/$/, ''), // Remove trailing slash
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Enable credentials for all requests
  timeout: 30000 // Set a 30 second timeout by default
});

// Add a direct test function to diagnose API connectivity issues
export const testDirectConnection = async () => {
  try {
    apiLogger.debug('Testing direct connection to API');
    // Try to connect directly without using the api instance
    const response = await axios({
      method: 'get',
      url: `${API_URL}/api/health`,
      timeout: 10000,
      withCredentials: true
    });
    apiLogger.debug('Direct connection test succeeded:', response.data);
    return {
      success: true,
      data: response.data,
      message: 'Direct connection successful'
    };
  } catch (error) {
    apiLogger.error('Direct connection test failed:', error);
    return {
      success: false,
      error: error.message,
      status: error.response?.status,
      message: 'Direct connection failed'
    };
  }
};

// Add a request interceptor to include the auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    apiLogger.log('Making request to:', config.baseURL + config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If we already tried a fallback, don't try again to avoid infinite loops
    if (originalRequest._retry || originalRequest._fallback) {
      return Promise.reject(error);
    }
    
    // Check if it's a 404 or 500 error
    if (error.response && (error.response.status === 404 || error.response.status === 500)) {
      apiLogger.warn(`API Error (${error.response.status}) for ${originalRequest.url}. Attempting fallback...`);
      
      // Get the URL without query parameters for easier matching
      const url = originalRequest.url.split('?')[0];
      const queryParams = originalRequest.url.includes('?') ? originalRequest.url.split('?')[1] : '';
      apiLogger.debug('Processing URL for fallback:', url);
      
      // Special direct mappings for problematic endpoints
      const directMappings = {
        // Dashboard endpoints - these are the ones we're seeing in the error logs
        '/api/users/tasks/active': '/api/tasks',
        '/api/users/daily-checklist/incomplete': '/api/daily-checklist',
        '/api/users/users/tasks/active': '/api/tasks',
        '/api/users/users/daily-checklist/incomplete': '/api/daily-checklist',
        
        // Onboarding endpoint that's failing
        '/api/onboarding/content/introduction': '/api/content/introduction',
        
        // Versions without /api/ prefix
        '/users/tasks/active': '/tasks',
        '/users/daily-checklist/incomplete': '/daily-checklist',
        '/onboarding/content/introduction': '/content/introduction'
      };
      
      // Find a fallback path
      let fallbackPath = null;
      
      // Try direct mapping first
      if (directMappings[url]) {
        fallbackPath = directMappings[url];
        apiLogger.debug(`Found direct mapping for ${url} -> ${fallbackPath}`);
      } else {
        // If no direct mapping, try removing /api/ prefix
        if (url.startsWith('/api/')) {
          const urlWithoutApi = url.replace('/api/', '/');
          if (directMappings[urlWithoutApi]) {
            fallbackPath = directMappings[urlWithoutApi];
            // Add back /api/ prefix if needed
            if (!fallbackPath.startsWith('/api/') && url.startsWith('/api/')) {
              fallbackPath = '/api' + fallbackPath;
            }
            apiLogger.debug(`Found mapping by removing /api/ prefix: ${url} -> ${fallbackPath}`);
          }
        }
        
        // If still no fallback, check for duplicate /users/ segments
        if (!fallbackPath && url.includes('/users/users/')) {
          fallbackPath = url.replace('/users/users/', '/users/');
          apiLogger.debug(`Fixed duplicate users segment: ${url} -> ${fallbackPath}`);
        }
      }
      
      // If we found a fallback path, try it
      if (fallbackPath) {
        // Add back query parameters if they existed
        if (queryParams) {
          fallbackPath = `${fallbackPath}?${queryParams}`;
        }
        
        apiLogger.debug(`Trying fallback path: ${fallbackPath}`);
        originalRequest.url = fallbackPath;
        originalRequest._fallback = true;
        
        try {
          return await axios(originalRequest);
        } catch (fallbackError) {
          apiLogger.error(`Fallback path ${fallbackPath} also failed:`, fallbackError);
          
          // Try one more fallback - remove /api/ prefix if it exists
          if (fallbackPath.includes('/api/')) {
            const secondFallbackPath = fallbackPath.replace('/api/', '/');
            apiLogger.debug(`Trying second fallback path without /api/ prefix: ${secondFallbackPath}`);
            originalRequest.url = secondFallbackPath;
            
            try {
              return await axios(originalRequest);
            } catch (secondFallbackError) {
              apiLogger.error(`Second fallback path ${secondFallbackPath} also failed:`, secondFallbackError);
              
              // Last resort - try a simpler path
              const simplePath = url.replace('/api/users/', '/api/').replace('/users/', '/');
              if (simplePath !== url) {
                apiLogger.debug(`Trying simplified path: ${simplePath}`);
                originalRequest.url = simplePath + (queryParams ? `?${queryParams}` : '');
                
                try {
                  return await axios(originalRequest);
                } catch (lastError) {
                  apiLogger.error(`Simplified path ${simplePath} also failed:`, lastError);
                  return Promise.reject(lastError);
                }
              }
              
              return Promise.reject(secondFallbackError);
            }
          }
          
          return Promise.reject(fallbackError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export const registerUser = async (userData) => {
  try {
    const response = await api.post('/api/users/register', userData);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in registerUser:', error);
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await api.post('/api/users/login', credentials);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in loginUser:', error);
    throw error;
  }
};

export const getProfile = async (userId) => {
  try {
    // Try multiple approaches to get the profile
    try {
      apiLogger.debug('Attempting to get profile with standard endpoint');
      const response = await api.get(`/api/users/profile`);
      return response.data;
    } catch (primaryError) {
      apiLogger.warn('Primary profile endpoint failed:', primaryError.message);
      
      // Try the user-specific endpoint as fallback
      try {
        apiLogger.debug('Attempting to get profile with user-specific endpoint');
        if (!userId) {
          throw new Error('User ID not available for fallback endpoint');
        }
        const fallbackResponse = await api.get(`/api/users/${userId}`);
        return fallbackResponse.data;
      } catch (fallbackError) {
        apiLogger.error('Error in getProfile (all methods failed):', fallbackError);
        throw new Error('Failed to fetch profile data');
      }
    }
  } catch (error) {
    apiLogger.error('Error in getProfile (all methods failed):', error);
    throw error;
  }
};

export const updateProfile = async (userId, updatedData) => {
  try {
    const response = await api.put(`/api/users/${userId}`, updatedData);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in updateProfile:', error);
    throw error;
  }
};

export const createRole = async (userId, roleData) => {
  try {
    const response = await api.post(`/api/users/${userId}/roles`, roleData);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in createRole:', error);
    throw error;
  }
};

export const getRoles = async (userId) => {
  try {
    const response = await api.get(`/api/users/${userId}/roles`);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in getRoles:', error);
    throw error;
  }
};

export const updateRole = async (userId, roleId, roleData) => {
  try {
    const response = await api.put(`/api/users/${userId}/roles/${roleId}`, roleData);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in updateRole:', error);
    throw error;
  }
};

export const deleteRole = async (userId, roleId) => {
  try {
    const response = await api.delete(`/api/users/${userId}/roles/${roleId}`);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in deleteRole:', error);
    throw error;
  }
};

export const changePassword = async (passwordData) => {
  try {
    const response = await api.post('/api/users/change-password', passwordData);
    return response.data;
  } catch (error) {
    apiLogger.error('Error in changePassword:', error);
    throw error;
  }
};

export const updateUserProfile = async (userData) => {
  try {
    // Store the data in localStorage first as a backup
    try {
      localStorage.setItem('userProfileBackup', JSON.stringify(userData));
      apiLogger.debug('Profile data backed up to localStorage');
    } catch (storageError) {
      apiLogger.warn('Failed to backup profile to localStorage:', storageError);
    }
    
    // Try multiple approaches to update the profile
    const userId = localStorage.getItem('userId');
    const errors = [];
    
    // First try the standard endpoint
    try {
      apiLogger.debug('Attempting profile update with standard endpoint');
      const response = await api.put('/api/users/profile', userData);
      apiLogger.debug('Standard endpoint succeeded');
      return response.data;
    } catch (primaryError) {
      apiLogger.warn('Primary profile update endpoint failed:', primaryError.message);
      errors.push({ endpoint: '/api/users/profile', error: primaryError });
      
      // Try the user-specific endpoint as fallback
      try {
        apiLogger.debug('Attempting profile update with user-specific endpoint');
        if (!userId) {
          throw new Error('User ID not available for fallback endpoint');
        }
        const fallbackResponse = await api.put(`/api/users/${userId}`, userData);
        apiLogger.debug('User-specific endpoint succeeded');
        return fallbackResponse.data;
      } catch (fallbackError) {
        apiLogger.error('Fallback profile update endpoint failed:', fallbackError.message);
        errors.push({ endpoint: `/api/users/${userId}`, error: fallbackError });
        
        // Try a direct POST to info endpoint
        try {
          apiLogger.debug('Attempting profile update with info endpoint');
          const infoResponse = await api.post('/api/users/info', { 
            ...userData, 
            userId: userId,
            updateType: 'profile'
          });
          apiLogger.debug('Info endpoint succeeded');
          return infoResponse.data;
        } catch (infoError) {
          apiLogger.error('Info endpoint failed:', infoError.message);
          errors.push({ endpoint: '/api/users/info', error: infoError });
          
          // Try a PATCH to profile endpoint
          try {
            apiLogger.debug('Attempting profile update with PATCH method');
            const patchResponse = await api.patch('/api/users/profile', userData);
            apiLogger.debug('PATCH endpoint succeeded');
            return patchResponse.data;
          } catch (patchError) {
            apiLogger.error('PATCH endpoint failed:', patchError.message);
            errors.push({ endpoint: 'PATCH /api/users/profile', error: patchError });
            
            // Try a direct POST to profile endpoint
            try {
              apiLogger.debug('Attempting profile update with direct POST to profile');
              const postResponse = await api.post('/api/users/profile', {
                ...userData,
                userId: userId,
                action: 'update'
              });
              apiLogger.debug('POST to profile endpoint succeeded');
              return postResponse.data;
            } catch (postError) {
              apiLogger.error('POST to profile endpoint failed:', postError.message);
              errors.push({ endpoint: 'POST /api/users/profile', error: postError });
              
              apiLogger.error('All profile update endpoints failed:', errors);
              throw new Error('Failed to update profile after trying all available endpoints');
            }
          }
        }
      }
    }
  } catch (error) {
    apiLogger.error('Error in updateUserProfile (all methods failed):', error);
    
    // Return a special error object that indicates we should use local storage
    return {
      success: false,
      error: error.message,
      useLocalStorage: true,
      localData: userData
    };
  }
};

// You can add more API functions here as needed

export default api;
